import { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { fetchConsents } from '../../store/actions/consent';
import { getOperationalLogById, editOperationalLog, fetchOperationalLogs } from '../../store/actions/operationalLog';
import { Loader } from '../../utils/tools';
import { Form, Row, Col, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { FACILITY_TYPE_OPTIONS } from '../../utils/constants/facilityTypes';
import { formValues, validation, LOADING_CODE_OPTIONS } from './validationSchema';

const EditOperationalLog = ({ operationalLogId, onClose }) => {
    const dispatch = useDispatch();
    const currentBranch = useSelector(state => state.branch.current);
    const consents = useSelector(state => state.consent.data);
    const consentsLoading = useSelector(state => state.consent.loading);
    const logToEdit = useSelector(state => state.operationalLog.logToEdit);
    const logToEditLoading = useSelector(state => state.operationalLog.logToEditLoading);

    useEffect(() => {
        dispatch(fetchConsents());
        dispatch(getOperationalLogById(operationalLogId));
    }, [dispatch, operationalLogId]);

    const formik = useFormik({
        initialValues: formValues,
        validationSchema: validation,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: (values, { setSubmitting }) => {
            dispatch(editOperationalLog({ operationalLogId, values }))
                .unwrap()
                .then(() => {
                    dispatch(fetchOperationalLogs({ branchId: currentBranch }));
                    onClose();
                })
                .catch(() => {})
                .finally(() => setSubmitting(false));
        },
    });

    useEffect(() => {
        if (logToEdit && logToEdit._id === operationalLogId) {
            formik.setValues({
                branchId: logToEdit.branch?._id || '',
                facilityType: logToEdit.facilityType || '',
                consentId: logToEdit.consent?._id || '',
                loadingCode: logToEdit.loadingCode || '',
                name: logToEdit.name || '',
                responsiblePersons: logToEdit.responsiblePersons?.length ? logToEdit.responsiblePersons : [''],
                defaultResponsiblePersons: logToEdit.defaultResponsiblePersons ?? [],
                defaultOperatingHoursFrom: logToEdit.defaultOperatingHoursFrom || '',
                defaultOperatingHoursTo: logToEdit.defaultOperatingHoursTo || '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logToEdit, operationalLogId]);

    const availableConsents = useMemo(() => {
        if (!Array.isArray(consents)) return [];
        return consents.filter((consent) =>
            consent.scope?.type === 'tenant' || consent.scope?.branch?._id === formik.values.branchId
        );
    }, [consents, formik.values.branchId]);

    if (consentsLoading || logToEditLoading) return <Loader />;

    return (
        <Form onSubmit={formik.handleSubmit}>
            <h2>Upraviť prevádzkový denník</h2>
            <hr />

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formName">
                        <Form.Label>Názov denníka *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Názov denníka"
                            {...formik.getFieldProps('name')}
                            isInvalid={formik.touched.name && !!formik.errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formFacilityType">
                        <Form.Label>Typ zariadenia *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte typ zariadenia"
                            {...formik.getFieldProps('facilityType')}
                            isInvalid={formik.touched.facilityType && !!formik.errors.facilityType}
                            disabled
                        >
                            <option value="">Vyberte typ zariadenia:</option>
                            {FACILITY_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.facilityType}</Form.Control.Feedback>
                        <Form.Text className="text-muted d-flex align-items-center gap-1 mt-1">
                            <FaInfoCircle />
                            <em>Typ zariadenia sa po vytvorení denníka nedá zmeniť.</em>
                        </Form.Text>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formLoadingCode">
                        <Form.Label>Kód nakladania *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte kód nakladania"
                            {...formik.getFieldProps('loadingCode')}
                            isInvalid={formik.touched.loadingCode && !!formik.errors.loadingCode}
                        >
                            <option value="">Vyberte kód nakladania:</option>
                            {LOADING_CODE_OPTIONS.map((group) => (
                                <optgroup key={group.label} label={group.label}>
                                    {group.options.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.loadingCode}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formConsent">
                        <Form.Label>Súhlas *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte súhlas"
                            {...formik.getFieldProps('consentId')}
                            isInvalid={formik.touched.consentId && !!formik.errors.consentId}
                        >
                            <option value="">Vyberte súhlas:</option>
                            {availableConsents.map((consent) => (
                                <option key={consent._id} value={consent._id}>
                                    {consent.name} ({consent.decisionNumber})
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.consentId}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Predvolené časové využitie zariadenia</Form.Label>
                        <Row className="g-2 align-items-start">
                            <Col xs={5} sm={4} md={3}>
                                <Form.Control
                                    type="time"
                                    aria-label="Predvolené časové využitie od"
                                    {...formik.getFieldProps('defaultOperatingHoursFrom')}
                                    isInvalid={formik.touched.defaultOperatingHoursFrom && !!formik.errors.defaultOperatingHoursFrom}
                                />
                                <Form.Control.Feedback type="invalid">{formik.errors.defaultOperatingHoursFrom}</Form.Control.Feedback>
                            </Col>
                            <Col xs="auto" className="d-flex justify-content-center text-muted" style={{ paddingTop: '7px' }}>
                                –
                            </Col>
                            <Col xs={5} sm={4} md={3}>
                                <Form.Control
                                    type="time"
                                    aria-label="Predvolené časové využitie do"
                                    {...formik.getFieldProps('defaultOperatingHoursTo')}
                                    isInvalid={formik.touched.defaultOperatingHoursTo && !!formik.errors.defaultOperatingHoursTo}
                                />
                                <Form.Control.Feedback type="invalid">{formik.errors.defaultOperatingHoursTo}</Form.Control.Feedback>
                            </Col>
                        </Row>
                        <Form.Text className="text-muted d-flex align-items-center gap-1 mt-1">
                            <FaInfoCircle />
                            <em>Predvyplní sa v nových denných záznamoch, dá sa zmeniť.</em>
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Zodpovední zamestnanci</Form.Label>
                        {formik.values.responsiblePersons.map((person, index) => (
                            <div key={index} className="d-flex align-items-center gap-2 mb-2">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Nastaviť ako predvoleného</Tooltip>}>
                                    <Form.Check
                                        type="checkbox"
                                        className="flex-shrink-0"
                                        checked={!!person?.trim() && formik.values.defaultResponsiblePersons.includes(person)}
                                        onChange={(e) => {
                                            const next = e.target.checked
                                                ? [...formik.values.defaultResponsiblePersons, person]
                                                : formik.values.defaultResponsiblePersons.filter((p) => p !== person);
                                            formik.setFieldValue('defaultResponsiblePersons', next);
                                        }}
                                    />
                                </OverlayTrigger>
                                <Form.Control
                                    type="text"
                                    placeholder="Meno a priezvisko"
                                    value={person}
                                    onChange={(e) => {
                                        const next = [...formik.values.responsiblePersons];
                                        next[index] = e.target.value;
                                        formik.setFieldValue('responsiblePersons', next);
                                        if (formik.values.defaultResponsiblePersons.includes(person) && person?.trim()) {
                                            formik.setFieldValue(
                                                'defaultResponsiblePersons',
                                                formik.values.defaultResponsiblePersons.map((p) => (p === person ? e.target.value : p))
                                            );
                                        }
                                    }}
                                />
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    type="button"
                                    className="flex-shrink-0"
                                    onClick={() => {
                                        formik.setFieldValue('responsiblePersons', formik.values.responsiblePersons.filter((_, i) => i !== index));
                                        formik.setFieldValue('defaultResponsiblePersons', formik.values.defaultResponsiblePersons.filter((p) => p !== person));
                                    }}
                                >
                                    Vymazať
                                </Button>
                            </div>
                        ))}
                        <Button
                            variant="outline-primary"
                            size="sm"
                            type="button"
                            onClick={() => formik.setFieldValue('responsiblePersons', [...formik.values.responsiblePersons, ''])}
                        >
                            + Pridať zamestnanca
                        </Button>
                        <Form.Text className="text-muted d-flex align-items-center gap-1 mt-2">
                            <FaInfoCircle />
                            <em>Zvolení predvolení zamestnanci sa predvyplnia v nových denných záznamoch, dá sa zmeniť.</em>
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-auto">
                <hr />
                <Col className="d-flex justify-content-end">
                    <Button
                        className="button-custom-large"
                        variant="primary"
                        type="submit"
                        size="lg"
                        style={{ marginTop: 'auto' }}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? <Loader /> : 'Uložiť zmeny'}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default EditOperationalLog;
